import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function SupplierRegister() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [supplier, setSupplier] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let ignore = false;
    if (!token) { setError("Lien d'invitation manquant."); setLoading(false); return; }
    api.get(`/supplier-invite/${token}`)
      .then((r) => { if (!ignore) setSupplier(r.data); })
      .catch(() => { if (!ignore) setError("Cette invitation est invalide ou a expiré. Demandez un nouveau lien."); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/supplier-invite/${token}`, { password });
      if (data.already_registered) navigate("/login");
      else navigate("/login?inscription=1");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-left">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between bg-[#0a0a0a] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "42px 42px" }} />
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, #FF6B35, transparent 70%)" }} />
        <div className="relative"><img src="/innova-fact-white.png" alt="innova-fact" className="h-14 w-auto" /></div>
        <div className="relative">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tighter leading-none">
            Bienvenue au<br />portail <span className="text-primary">fournisseur.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-sm leading-relaxed">
            Créez votre accès pour consulter vos factures et vos confirmations de paiement en tout temps.
          </p>
        </div>
        <div className="relative text-xs text-white/50 tracking-wider uppercase">Accès sécurisé</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-2 mb-8"><img src="/innova-fact-dark.png" alt="innova-fact" className="h-9 w-auto" /></div>

          {loading ? (
            <div className="text-sm text-muted-foreground tracking-widest uppercase animate-pulse">Vérification du lien…</div>
          ) : !supplier ? (
            <div data-testid="invite-error" className="space-y-4">
              <h2 className="font-heading text-2xl font-bold tracking-tight">Lien invalide</h2>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" className="rounded-sm" onClick={() => navigate("/login")}>Retour à la connexion</Button>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Créer mon compte</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">
                {supplier.supplier_name} · <span className="font-medium">{supplier.supplier_email}</span>
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pw" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mot de passe</Label>
                  <Input id="pw" data-testid="register-password" type="password" required className="rounded-sm h-11" placeholder="min. 8 caractères" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cpw" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirmer le mot de passe</Label>
                  <Input id="cpw" data-testid="register-confirm" type="password" required className="rounded-sm h-11" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                {error && <div data-testid="register-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2">{error}</div>}
                <Button type="submit" data-testid="register-submit" disabled={submitting} className="w-full h-11 rounded-sm group">
                  {submitting ? "Création…" : "Créer mon compte"}
                  <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1"><CheckCircle size={14} className="text-emerald-600" /> Vous pourrez ensuite vous connecter avec votre courriel et ce mot de passe.</p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
